import {Dispatch, PropsWithChildren, useMemo} from "react";
import {
  createSessionReducer,
  initialCreateSessionState,
} from "../../reducers/create-session";
import {createContext} from "../../../../../context/create-context";
import {
  CreateSessionActions,
  CreateSessionState,
} from "../../reducers/create-session/types";
import {usePricingCurrencies} from "@/features/currencies";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {
  OFFERING_CREATE_DRAFT_KEYS,
  usePersistedReducer,
} from "../../../../../utils/offering-create-draft";

type CreateSessionContextState = {
  state: typeof initialCreateSessionState;
  dispatch: Dispatch<CreateSessionActions>;
  wasDraftRestored: boolean;
};

const [useContext, ContextProvider] = createContext<CreateSessionContextState>();

export const useCreateSessionContext = useContext;

export const CreateSessionProvider = ({children}: PropsWithChildren) => {
  const {state: profileState} = useGetProfileContext();
  const draftScope = useMemo(
    () => ({
      draftKey: OFFERING_CREATE_DRAFT_KEYS.session,
      email: profileState.profileData.email,
    }),
    [profileState.profileData.email]
  );

  const [state, dispatch, {wasDraftRestored}] = usePersistedReducer(
    createSessionReducer,
    draftScope,
    () => initialCreateSessionState
  );
  const pricingCurrencies = usePricingCurrencies();

  const newState: CreateSessionState = {
    currentCreateSessionStep: state.currentCreateSessionStep,
    formData: {
      ...state.formData,
      callPriceCurrency:
        state.formData.callPriceCurrency ?? pricingCurrencies.formCurrencies[0],
      callPriceType:
        state.formData.callPriceType ??
        (pricingCurrencies.connectedBankStatus.isBankConnected ? "paid" : "free"),
    },
    sessionData: state.sessionData,
  };

  return (
    <ContextProvider value={{state: newState, dispatch, wasDraftRestored}}>
      {children}
    </ContextProvider>
  );
};
